// src/store/websiteStore.js - aktualizovaný
import { create } from 'zustand';
import { websiteService } from '../api/services/websites';

import { useToastStore } from '../components/UI/Toast';

const useWebsiteStore = create((set, get) => ({
    websites: [],
    currentWebsite: null,
    monitoringResults: null,
    loading: false,
    error: null,


    // Načtení seznamu webů s rozšířenými daty
    fetchWebsites: async () => {
        set({ loading: true, error: null });
        try {
            const { data } = await websiteService.getWebsites();

            // Pro každý web získáme základní monitorovací data, pokud existují
            const enhancedWebsites = await Promise.all(data.map(async (website) => {
                try {
                    // Získání základních metrik
                    const uptimeData = website?.monitoringResults?.uptime;
                    const securityData = website?.monitoringResults?.security;
                    const performanceData = website?.monitoringResults?.performance;

                    // Výpočet uptime procent
                    const uptimePercentage = uptimeData?.details
                        ? (uptimeData.details.status_code === 200 ? 100 : 0)
                        : null;

                    // Výpočet bezpečnostního skóre
                    let securityScore = null;
                    if (securityData?.details) {
                        const issues = securityData.details.issues || [];
                        let score = 100;
                        issues.forEach(issue => {
                            if (issue.severity === 'critical') score -= 20;
                            else if (issue.severity === 'high') score -= 15;
                            else if (issue.severity === 'medium') score -= 10;
                            else if (issue.severity === 'low') score -= 5;
                        });
                        securityScore = Math.max(0, score);
                    }

                    // Výpočet výkonnostního skóre
                    const performanceScore = performanceData?.details?.metrics
                        ? performanceData.details.metrics.performance_score
                        : null;

                    // Vytvoříme aktualizovaný web
                    const updatedWebsite = {
                        ...website,
                        uptimePercentage: uptimePercentage !== null ? uptimePercentage : 'N/A',
                        securityScore: securityScore !== null ? securityScore : 'N/A',
                        performanceScore: performanceScore !== null ? performanceScore : 'N/A'
                    };

                    // Vracíme rozšířený objekt webu
                    return {
                        ...website,
                        uptimePercentage: uptimePercentage !== null ? uptimePercentage : 'N/A',
                        securityScore: securityScore !== null ? securityScore : 'N/A',
                        performanceScore: performanceScore !== null ? performanceScore : 'N/A'
                    };
                } catch (error) {
                    console.warn(`Couldn't fetch metrics for website ${website.id}:`, error);
                    return website;
                }
            }));

            set({ websites: enhancedWebsites, loading: false });
        } catch (error) {
            console.error('Error fetching websites:', error);
            set({
                error: error.response?.data?.message || 'Načtení webů selhalo',
                loading: false
            });
        }
    },

    // Přidání nového webu
    addWebsite: async (websiteData) => {
        set({ loading: true, error: null });
        try {
            const { data } = await websiteService.addWebsite(websiteData);
            set({
                websites: [...get().websites, data.website],
                loading: false
            });
            useToastStore.getState().addToast('Web byl úspěšně přidán', 'success');
            return data.website;
        } catch (error) {
            console.error('Error adding website:', error);
            set({
                error: error.response?.data?.message || 'Přidání webu selhalo',
                loading: false
            });
            useToastStore.getState().addToast('Přidání webu selhalo', 'danger');
            throw error;
        }
    },

    // Odstranění webu s potvrzujícím toastem
    removeWebsite: async (id) => {
        set({ error: null });
        try {
            await websiteService.removeWebsite(id);
            set({ websites: get().websites.filter(website => website.id !== id) });
            useToastStore.getState().addToast('Web byl úspěšně odstraněn', 'success');
        } catch (error) {
            console.error('Error removing website:', error);
            set({ error: error.response?.data?.message || 'Odstranění webu selhalo' });
            useToastStore.getState().addToast('Odstranění webu selhalo', 'danger');
            throw error;
        }
    },

    // Aktualizace jednoho webu - užitečné pro obnovení dat bez načítání všech webů
    refreshWebsite: async (id) => {
        set({ loading: true, error: null });
        try {
            // Získáme detail jednoho webu
            const { data: website } = await websiteService.getWebsiteDetails(id);

            // Získání základních metrik

            const uptimeData = website?.monitoringResults?.uptime;
            const securityData = website?.monitoringResults?.security;
            const performanceData = website?.monitoringResults?.performance;

            // Výpočet uptime procent
            const uptimePercentage = uptimeData?.details
                ? (uptimeData.details.status_code === 200 ? 100 : 0)
                : null;

            // Výpočet bezpečnostního skóre
            let securityScore = null;
            if (securityData?.details) {
                const issues = securityData.details.issues || [];
                let score = 100;
                issues.forEach(issue => {
                    if (issue.severity === 'critical') score -= 20;
                    else if (issue.severity === 'high') score -= 15;
                    else if (issue.severity === 'medium') score -= 10;
                    else if (issue.severity === 'low') score -= 5;
                });
                securityScore = Math.max(0, score);
            }

            // Výpočet výkonnostního skóre
            const performanceScore = performanceData?.details?.metrics
                ? performanceData.details.metrics.performance_score
                : null;

            // Vytvoříme aktualizovaný web
            const updatedWebsite = {
                ...website,
                uptimePercentage: uptimePercentage !== null ? uptimePercentage : 'N/A',
                securityScore: securityScore !== null ? securityScore : 'N/A',
                performanceScore: performanceScore !== null ? performanceScore : 'N/A'
            };

            // Aktualizujeme pole webů
            set({
                websites: get().websites.map(w => w.id === id ? updatedWebsite : w),
                loading: false
            });

            return updatedWebsite;
        } catch (error) {
            console.error('Error refreshing website:', error);
            set({
                error: error.response?.data?.message || 'Aktualizace webu selhala',
                loading: false
            });
            throw error;
        }
    },

    // Spuštění kontroly webů s okamžitým zobrazením stavu
    runMonitoringCheck: async (id, type) => {
        try {
            useToastStore.getState().addToast(`Spouštím kontrolu: ${type}`, 'info');
            await websiteService.runMonitoring(id, type);
            useToastStore.getState().addToast(`Kontrola ${type} byla spuštěna`, 'success');

            // Po krátké prodlevě obnovíme data webu
            setTimeout(async () => {
                await get().refreshWebsite(id);
            }, 3000);

            return true;
        } catch (error) {
            console.error(`Error running ${type} check:`, error);
            useToastStore.getState().addToast(`Kontrola ${type} selhala`, 'danger');
            throw error;
        }
    },

    // Přidání nového webu
    addWebsite: async (websiteData) => {
        set({ loading: true, error: null });
        try {
            const { data } = await websiteService.addWebsite(websiteData);
            set({
                websites: [...get().websites, data.website],
                loading: false
            });
            return data.website;
        } catch (error) {
            console.error('Error adding website:', error);
            set({
                error: error.response?.data?.message || 'Přidání webu selhalo',
                loading: false
            });
            throw error;
        }
    },

    // Načtení dat pro konkrétní typ monitoringu
    fetchSpecificData: async (id, dataType) => {
        set({ loading: true, error: null });
        try {
            const response = await websiteService.getMonitoringResults(id, dataType);
            const data = response?.data;

            if (!data || (Array.isArray(data) && data.length === 0)) {
                console.warn(`No ${dataType} data available for website ${id}`);
                return null;
            }

            set({ loading: false });
            return data;
        } catch (error) {
            websiteService.logApiError(error, `monitoring/${dataType}`);
            set({
                error: error.response?.data?.message || `Načtení ${dataType} dat selhalo`,
                loading: false
            });
            return null;
        }
    },

    // Odstranění webu
    removeWebsite: async (id) => {
        set({ error: null });
        try {
            await websiteService.removeWebsite(id);
            set({ websites: get().websites.filter(website => website.id !== id) });
        } catch (error) {
            console.error('Error removing website:', error);
            set({ error: error.response?.data?.message || 'Odstranění webu selhalo' });
            throw error;
        }
    },

    // Nastavení aktuálního webu
    setCurrentWebsite: (website) => {
        set({ currentWebsite: website });
    },

    // Načtení výsledků monitorování
    fetchMonitoringResults: async (id) => {
        set({ loading: true, error: null });
        try {
            // Načtení všech typů monitorování pro web
            const types = ['uptime', 'performance', 'security', 'seo'];
            const results = {};

            // Paralelní načtení dat pro všechny typy
            await Promise.all(types.map(async (type) => {
                try {
                    const response = await websiteService.getMonitoringResults(id, type);
                    if (response && response.data) {
                        if (Array.isArray(response.data) && response.data.length > 0) {
                            // Pokud máme pole výsledků, použijeme první (nejnovější)
                            const latestResult = response.data[0];
                            results[type] = latestResult.details || {};
                        } else {
                            // Pokud není pole, použijeme přímo data
                            results[type] = response.data;
                        }
                    }
                } catch (err) {
                    websiteService.logApiError(err, `monitoring/${type}`);
                    console.warn(`Could not load ${type} monitoring results`);
                }
            }));

            // Defaultně prázdné pole pro případné problémy
            results.issues = results.issues || [];

            set({ monitoringResults: results, loading: false });
            return results;
        } catch (error) {
            websiteService.logApiError(error, 'monitoring/results');
            set({
                error: error.response?.data?.message || 'Načtení výsledků monitorování selhalo',
                loading: false
            });
            throw error;
        }
    },

    // Spuštění bezpečnostní kontroly
    runSecurityCheck: async (id) => {
        set({ loading: true, error: null });
        try {
            const { data } = await websiteService.runSecurityCheck(id);
            set({ loading: false });
            return data;
        } catch (error) {
            console.error('Error running security check:', error);
            set({
                error: error.response?.data?.message || 'Bezpečnostní kontrola selhala',
                loading: false
            });
            throw error;
        }
    },

    // Spuštění SEO kontroly
    runSeoCheck: async (id) => {
        set({ loading: true, error: null });
        try {
            const { data } = await websiteService.runSeoCheck(id);
            set({ loading: false });
            return data;
        } catch (error) {
            console.error('Error running SEO check:', error);
            set({
                error: error.response?.data?.message || 'SEO kontrola selhala',
                loading: false
            });
            throw error;
        }
    },

    // Spuštění kontroly výkonu
    runPerformanceCheck: async (id) => {
        set({ loading: true, error: null });
        try {
            const { data } = await websiteService.runPerformanceCheck(id);
            set({ loading: false });
            return data;
        } catch (error) {
            console.error('Error running performance check:', error);
            set({
                error: error.response?.data?.message || 'Kontrola výkonu selhala',
                loading: false
            });
            throw error;
        }
    },

    // Vyčištění stavu store
    clearState: () => {
        set({
            currentWebsite: null,
            monitoringResults: null,
            error: null
        });
    }
}));

export default useWebsiteStore;